import { datadogLogger } from '../../shared/datadog/logger';

/**
 * Context structure for agent-related information
 */
interface AgentContext {
    agentId?: string;
    agencyId?: string;
}

/**
 * Extract agent context from agent object
 */
function extractAgentContext(agent?: { id: string; agency?: { id: string } } | null): AgentContext {
    if (!agent) {
        return {};
    }

    return {
        agentId: agent.id,
        agencyId: agent.agency?.id,
    };
}

/**
 * Build enriched log context with agent information
 *
 * @param specificContext - Specific context for this log entry
 * @param agent - Optional agent object to extract context from
 * @returns Combined context with agent information
 */
function buildLogContext(
    specificContext: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): Record<string, unknown> {
    return {
        ...specificContext,
        ...extractAgentContext(agent),
    };
}

/**
 * Log modal opening event
 */
export function logModalOpened(
    modalName: string,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.info(`${modalName} opened`, buildLogContext(context, agent));
}

/**
 * Log modal closing event
 */
export function logModalClosed(
    modalName: string,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.info(`${modalName} closed`, buildLogContext(context, agent));
}

/**
 * Log user choice/action in modal
 */
export function logModalChoice(
    modalName: string,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.info(`${modalName} choice made`, buildLogContext(context, agent));
}

/**
 * Log reason selection
 */
export function logReasonSelected(
    modalName: string,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.info(`${modalName} reason selected`, buildLogContext(context, agent));
}

/**
 * Log successful mutation
 * TODO: temporary logging for monitoring - remove after validation period
 */
export function logMutationSuccess(
    operation: string,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.info(`Successfully ${operation}`, buildLogContext(context, agent));
}

/**
 * Log failed mutation
 */
export function logMutationError(
    operation: string,
    error: unknown,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.error(`Failed to ${operation}`, {
        ...buildLogContext(context, agent),
        error: error instanceof Error ? error.message : String(error),
    });
}

/**
 * Log query fetch error
 */
export function logQueryError(
    operation: string,
    error: unknown,
    context: Record<string, unknown> = {},
    agent?: { id: string; agency?: { id: string } } | null,
): void {
    datadogLogger.warn(`Failed to ${operation}`, {
        ...buildLogContext(context, agent),
        error: error instanceof Error ? error.message : String(error),
    });
}
